import React, { forwardRef } from "react";
import PropTypes from "prop-types";
import { DndProvider } from "react-dnd";
import {
  Tree,
  MultiBackend,
  getBackendOptions,
} from "@minoru/react-dnd-treeview";
import { NativeTypes } from "react-dnd-html5-backend";
import SFLoadingSpinner from "../loadingSpinner";
import getStyled from "../../hooks/getStyled";
import { getDropConditions } from "./dropConditions";
import TreeUtils from "./treeUtils";
import ListItemButtonRenderer from "./renderers/listItemButtonRenderer";
import DragPreviewRenderer from "./renderers/dragPreviewRenderer";

// A set of item types used in drop operations within the tree view.
// Each item type is identified by a unique string, and these identifiers are used by the onDrop callback
// to determine the behavior of the tree when dropping items of each type
const DRAGGED_ITEM_TYPES = {
  symbol: "Symbol()",
  text: "__NATIVE_TEXT__",
  // Add more item types as needed
};

const { styled } = getStyled();

const TreeContainerStyled = styled("div")(({ theme }) => ({
  ul: {
    paddingLeft: 0,
    listStyle: "none",
    ".MuiListItemIcon-root": {
      minWidth: 20,
      color: theme.palette.grey200.main,
    },
    ".MuiListItemButton-root": {
      "&.Mui-selected": {
        backgroundColor: theme.palette.blue50.main,
      },
    },
  },
}));

const Placeholder = ({ node, options }) => {
  return (
    <div
      style={{
        backgroundColor: "#747480",
        height: "2px",
        width: `calc(100% - ${16 * options.depth}px)`,
        marginLeft: `${16 * options.depth}px`,
      }}
    ></div>
  );
};

const SFTreeView = forwardRef(
  (
    {
      treeData,
      handleClick,
      handleDrop,
      selectedNodeIds,
      initialOpen,
      enableDragAndDrop = false,
      dropConfig = {},
      canDropFromExternal = () => true,
      handleDropExternal,
      handleChangeOpen = () => {},
      getButtonDefs,
    },
    ref
  ) => {
    if (treeData?.length === 0) return <SFLoadingSpinner />;

    // Callback function for determining where in the tree can a given node be dropped
    const canDrop = (currentTree, options) => {
      // Check if drag-and-drop is disabled, or if dropConfig is empty
      if (!enableDragAndDrop || Object.keys(dropConfig).length === 0) {
        return false;
      }

      const itemType = String(options.monitor.getItemType());
      if (itemType === "Symbol()") {
        // Calculate drop conditions
        const dropConditions = getDropConditions(
          currentTree,
          options,
          dropConfig
        );

        // Allow dropping according to met conditions
        return dropConditions.some((condition) => condition);
      } else if (itemType === "__NATIVE_TEXT__") {
        return canDropFromExternal(options);
      } else {
        return false;
      }
    };

    // Callback function for when the state of the tree is changed by drag-and-drop
    const onDrop = (newTree, options) => {
      // Get the item type of the dropped item from options.monitor and convert it to a string
      const itemType = String(options.monitor.getItemType());

      if (itemType === DRAGGED_ITEM_TYPES.symbol) {
        // If the item type of the dropped item is Symbol(), the dropped item is a tree node
        // Calculate the new depth of the dragged node in the tree after dropping it
        const newNodeDepth = TreeUtils.getNodeDepth(
          newTree,
          options.dragSource.id,
          0
        );

        if (options.dragSource.data.depth !== newNodeDepth) {
          // Update the depth of the dragged node only if it has changed (allowMoveToUpperLevel)
          const finalTree = newTree.map((node) =>
            node.id === options.dragSource.id
              ? { ...node, data: { ...node.data, depth: newNodeDepth } }
              : node
          );
          // Call the handleDrop function with the updated tree and options
          handleDrop(finalTree, options);
        } else {
          // Depth remains unchanged, no need to create an updated tree
          handleDrop(newTree, options);
        }
        //! By default, when you drop a node onto another node, it relocates the dragged node inside the node onto which you dropped it
        //! If you want to customize this behavior for particular scenarios, you should incorporate the desired logic within the handleDrop function
      } else if (itemType === DRAGGED_ITEM_TYPES.text) {
        // If the item type of the dropped item is __NATIVE_TEXT__, the dropped item is text from outside the tree view
        handleDropExternal(newTree, options);
        // You can include additional native item types within the condition and handle each type's drop in the handleDropExternal function passed as a prop
        // Ensure that any additional native item types are also included in the extraAcceptTypes prop of the tree view
      } else {
        return;
      }
    };

    // Function for building the button definitions for a given node using the getButtonDefs
    // callback function passed as a prop
    const buildButtonDefs = (node, options) => {
      let buttonDefs = [];
      if (getButtonDefs) {
        buttonDefs = getButtonDefs(node, options);
      }
      return buttonDefs;
    };

    return (
      <DndProvider backend={MultiBackend} options={getBackendOptions()}>
        <TreeContainerStyled>
          <Tree
            ref={ref}
            tree={treeData}
            rootId={null}
            onDrop={(newTree, options) => onDrop(newTree, options)}
            render={(node, options) => {
              return (
                <ListItemButtonRenderer
                  node={node}
                  options={options}
                  onClick={() => handleClick(node)}
                  selected={
                    // Todo: change this line once the tree view is updated to support multiple selected nodes
                    Array.isArray(selectedNodeIds)
                      ? selectedNodeIds.includes(node.id)
                      : selectedNodeIds === node.id
                  }
                  buttonDefs={buildButtonDefs(node, options)}
                />
              );
            }}
            canDrag={() => enableDragAndDrop}
            canDrop={(currentTree, options) => canDrop(currentTree, options)}
            dropTargetOffset={5}
            initialOpen={initialOpen}
            insertDroppableFirst={false}
            placeholderRender={(node, options) => (
              <Placeholder node={node} options={options} />
            )}
            sort={false}
            extraAcceptTypes={[NativeTypes.TEXT]}
            onChangeOpen={(nodeIds) => handleChangeOpen(nodeIds)}
            dragPreviewRender={(monitorProps) => {
              const { item } = monitorProps;
              return (
                <DragPreviewRenderer item={item} />
              );
            }}
          />
        </TreeContainerStyled>
      </DndProvider>
    );
  }
);

SFTreeView.propTypes = {
  treeData: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      text: PropTypes.string.isRequired,
      parent: PropTypes.oneOfType([PropTypes.number, PropTypes.string]), // Required according to React DnD TreeView docs, but SFTreeView level 0 nodes have null parent
      droppable: PropTypes.bool,
      data: PropTypes.shape({
        // Includes additional data properties for each node, according to the needs of the parent component
        depth: PropTypes.number.isRequired, // depth is required for fundamental calculations in SFTreeView
        disabled: PropTypes.bool, // If true, the node will be rendered as disabled (if not provided, the node will be rendered as enabled)
        hasChild: PropTypes.bool, // Not required but needed for the drag preview renderer styling to be precise
        // Add more data properties, according to the needs of the parent component
      }).isRequired,
    })
  ).isRequired,
  handleClick: PropTypes.func,
  handleDrop: PropTypes.func, // Callback function typically used for updating parent component state (e.g., handleDrop = (newTree, options) => setTreeData(newTree))
  selectedNodeIds: PropTypes.oneOfType([
    PropTypes.string, // Todo: remove this line once the tree view is updated to support multiple selected nodes
    PropTypes.number, // Todo: remove this line once the tree view is updated to support multiple selected nodes
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.arrayOf(PropTypes.number),
  ]),
  initialOpen: PropTypes.oneOfType([
    PropTypes.bool, // If true, all parent nodes will be initialized to the open state
    PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    ), // If an array of node IDs, only the specified parent nodes will be initialized in the open state
  ]),
  enableDragAndDrop: PropTypes.bool, // If true, enables drag-and-drop functionality
  dropConfig: PropTypes.shape({
    // Contains drag-and-drop configuration options. If no options are provided, the drag-and-drop functionality will not work even if enableDragAndDrop is true
    allowSiblingReorder: PropTypes.bool,
    allowMoveToSameLevel: PropTypes.bool,
    allowMoveToUpperLevel: PropTypes.bool,
    allowDropOnSibling: PropTypes.bool,
  }),
  canDropFromExternal: PropTypes.func, // Callback function for determining whether an external drag source can be dropped on a given node. Called inside the canDrop function
  handleDropExternal: PropTypes.func, // Callback function similar to handleDrop but for external drag sources
  handleChangeOpen: PropTypes.func, // Callback function for when a node is opened or closed. The function is passed an array of node IDs in the open state
  /*
    Callback function needed for rendering buttons for each node.
    The function is passed the node and options objects, and must return an array of button definitions.
    Each button definition is an object with the following properties:
    {
      icon: <IconComponent />, // The icon to be displayed on the button
      onClick: () => {}, // The callback function to be executed when the button is clicked
      tooltip: "Button tooltip", // The tooltip to be displayed on hover
      hidden: false, // If true, the button will not be rendered
    }
  */
  getButtonDefs: PropTypes.func,
};

export default SFTreeView;
